import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/client';
import { api } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import {
  COMMENT_MAX,
  DOMAINS_MAX,
  ORIGIN_MAX,
  domainRowsFrom,
  rowErrorsFromProblem,
  sameDomains,
  toEmbedDomains,
  validateDomainRows,
  type DomainRow,
  type OriginViolation,
} from '../shared/lib/embed-domains';
import { formatDateTime } from '../shared/lib/format';
import { useAsync } from '../shared/lib/use-async';
import {
  DISABLED_OPACITY,
  FormAlert,
  actionsStyle,
  errorStyle,
  hintStyle,
  supportCode,
} from '../shared/ui/form';
import {
  Card,
  ConfirmDialog,
  Field,
  primaryButtonStyle,
  secondaryButtonStyle,
  smallButtonStyle,
} from '../shared/ui/primitives';

/**
 * Домены встраивания представлений в CT (FR-635, FR-636) — раздел администратора.
 *
 * Список заменяется целиком (`PUT`), как того требует контракт: правка по одной записи
 * оставляет забытую старую. Поэтому и экран — редактор всего списка с одной кнопкой
 * «Сохранить», а не построчные «удалить» с немедленным действием.
 *
 * Рядом — число живых сессий встраивания: администратор видит его до того, как уберёт
 * домен, — открытые у CT представления продолжат работать до конца сессии.
 *
 * Пустой список — запрет встраивания всем; сохранение пустого списка подтверждается
 * отдельно, словами о последствии.
 */

const SESSIONS_REFRESH_MS = 30_000;

export function EmbedDomainsCard({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.embed;
  const domains = useAsync(() => api.embedDomains(), []);
  const sessions = useAsync(() => api.embedSessions(), [], { intervalMs: SESSIONS_REFRESH_MS });

  const [saved, setSaved] = useState<DomainRow[]>([]);
  const [rows, setRows] = useState<DomainRow[]>([]);
  const [rowErrors, setRowErrors] = useState<Readonly<Record<number, OriginViolation>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  useEffect(() => {
    if (domains.data !== null) {
      const loaded = domainRowsFrom(domains.data);
      setSaved(loaded);
      setRows(loaded);
    }
  }, [domains.data]);

  const dirty = !sameDomains(rows, saved);

  const change = (next: DomainRow[]): void => {
    setRows(next);
    setNotice(null);

    if (submitted) {
      setRowErrors(validateDomainRows(next));
    }
  };

  const save = async (): Promise<void> => {
    setPending(true);
    setGeneral(null);

    try {
      const result = await api.replaceEmbedDomains(toEmbedDomains(rows));
      const loaded = domainRowsFrom(result);

      setSaved(loaded);
      setRows(loaded);
      setSubmitted(false);
      setRowErrors({});
      setConfirmEmpty(false);
      setNotice(text.saved);
      sessions.reload();
    } catch (cause) {
      setConfirmEmpty(false);

      if (cause instanceof ApiError) {
        const mapped = rowErrorsFromProblem(rows, cause.problem.errors ?? []);
        setRowErrors(mapped);
        setGeneral(
          Object.keys(mapped).length > 0
            ? text.errors.rowsInvalid
            : cause.status === FORBIDDEN
              ? text.errors.forbidden
              : `${text.errors.failed} · ${messages.cabinet.drivers.errors.code}: ${supportCode(cause)}`,
        );
      } else {
        setGeneral(text.errors.failed);
      }
    } finally {
      setPending(false);
    }
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setSubmitted(true);
    setNotice(null);

    // Пустые строки, которые добавили и не заполнили, — не ошибка, а мусор: убираются.
    const filled = rows.filter((row) => row.origin.trim() !== '' || row.comment.trim() !== '');
    const found = validateDomainRows(filled);

    setRows(filled);
    setRowErrors(found);

    if (Object.keys(found).length > 0) {
      setGeneral(text.errors.rowsInvalid);

      return;
    }

    if (filled.length === 0 && saved.length > 0) {
      setConfirmEmpty(true);

      return;
    }

    void save();
  };

  const createdAt = new Map((domains.data ?? []).map((item) => [item.origin, item.created_at]));

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--sp-3)',
          justifyContent: 'space-between',
        }}
      >
        <h2>{text.title}</h2>
        <Field
          label={text.activeSessions}
          value={
            <span className="num">
              {sessions.error !== null
                ? messages.cabinet.common.error
                : sessions.data === null
                  ? messages.cabinet.common.loading
                  : sessions.data.active_count}
            </span>
          }
        />
      </div>

      <p style={{ margin: 0, ...hintStyle }}>{text.description}</p>

      {domains.error === null ? null : (
        <div role="alert" style={errorStyle}>
          {domains.error.status === FORBIDDEN
            ? messages.cabinet.common.forbidden
            : `${messages.cabinet.common.error}: ${domains.error.code}`}
        </div>
      )}

      {!canEdit ? (
        saved.length === 0 ? (
          <span style={hintStyle}>{text.empty}</span>
        ) : (
          <ul
            style={{ margin: 0, paddingLeft: 'var(--sp-5)', display: 'grid', gap: 'var(--sp-1)' }}
          >
            {saved.map((row) => (
              <li key={row.origin}>
                <span className="num">{row.origin}</span>
                {row.comment === '' ? null : <span style={hintStyle}>{` · ${row.comment}`}</span>}
              </li>
            ))}
          </ul>
        )
      ) : (
        <form
          noValidate
          onSubmit={submit}
          style={{ display: 'grid', gap: 'var(--sp-3)', minWidth: 0 }}
        >
          {rows.length === 0 ? <span style={hintStyle}>{text.empty}</span> : null}

          {rows.map((row, index) => {
            const error = rowErrors[index];
            const errorId = `embed-row-${index}-error`;
            const added = createdAt.get(row.origin);

            return (
              <div key={index} style={{ display: 'grid', gap: 'var(--sp-1)', minWidth: 0 }}>
                <div style={rowStyle}>
                  <input
                    className="num"
                    value={row.origin}
                    maxLength={ORIGIN_MAX}
                    placeholder={text.originPlaceholder}
                    aria-label={`${text.origin} ${index + 1}`}
                    aria-invalid={error !== undefined}
                    aria-describedby={error === undefined ? undefined : errorId}
                    autoComplete="off"
                    spellCheck={false}
                    style={{ flex: '2 1 220px', minWidth: 0 }}
                    onChange={(event) =>
                      change(
                        rows.map((item, position) =>
                          position === index ? { ...item, origin: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  <input
                    value={row.comment}
                    maxLength={COMMENT_MAX}
                    placeholder={text.commentPlaceholder}
                    aria-label={`${text.comment} ${index + 1}`}
                    autoComplete="off"
                    style={{ flex: '1 1 160px', minWidth: 0 }}
                    onChange={(event) =>
                      change(
                        rows.map((item, position) =>
                          position === index ? { ...item, comment: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  <button
                    type="button"
                    style={{ ...smallButtonStyle, minHeight: 'var(--field-h)' }}
                    aria-label={`${text.remove}: ${row.origin === '' ? index + 1 : row.origin}`}
                    onClick={() => change(rows.filter((_, position) => position !== index))}
                  >
                    {text.remove}
                  </button>
                </div>
                {error === undefined ? (
                  added === undefined ? null : (
                    <span className="num" style={hintStyle}>
                      {`${text.addedAt} ${formatDateTime(added)}`}
                    </span>
                  )
                ) : (
                  <span id={errorId} style={errorStyle}>
                    {text.errors[error]}
                  </span>
                )}
              </div>
            );
          })}

          <div>
            <button
              type="button"
              style={smallButtonStyle}
              disabled={rows.length >= DOMAINS_MAX}
              onClick={() => change([...rows, { origin: '', comment: '' }])}
            >
              {text.add}
            </button>
          </div>

          <span style={hintStyle}>{text.originHint}</span>

          <FormAlert text={general} />

          {notice === null ? null : (
            <div role="status">
              <Card tone="ok">{notice}</Card>
            </div>
          )}

          <div style={actionsStyle}>
            <button
              type="button"
              style={secondaryButtonStyle}
              disabled={!dirty || pending}
              onClick={() => {
                setRows(saved);
                setRowErrors({});
                setGeneral(null);
                setSubmitted(false);
              }}
            >
              {text.discard}
            </button>
            <button
              type="submit"
              disabled={!dirty || pending}
              aria-busy={pending}
              style={{
                ...primaryButtonStyle,
                opacity: !dirty || pending ? DISABLED_OPACITY : 1,
              }}
            >
              {pending ? text.saving : text.save}
            </button>
          </div>
        </form>
      )}

      {confirmEmpty ? (
        <ConfirmDialog
          title={text.confirmEmptyTitle}
          description={text.confirmEmpty}
          confirmLabel={text.confirmEmptyAction}
          pending={pending}
          onCancel={() => setConfirmEmpty(false)}
          onConfirm={() => void save()}
        />
      ) : null}
    </Card>
  );
}

const FORBIDDEN = 403;

// Строка переносится целиком на узком экране: источник и комментарий — по ширине, кнопка — следом.
const rowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-2)',
  alignItems: 'center',
} as const;
